import type { Series, Point } from 'highcharts';
import { UIChartColumn } from '../../components/column/ui-chart-column';
import { whenDOMReady } from '../../../shared-components/global/ui-helpers';
import type { ComponentPlugin } from '../../../shared-components/components/ui-element.type';
import { ExtendedPlotSeriesOptions } from '../../components/charts.types';

/** Highcharts internal SVG element subset used for data label manipulation. */
interface DataLabelElement {
    getAttribute(name: string): string | null;
    setAttribute(name: string, value: number | string): void;
}

/** Highcharts internal data label shape (not part of the public API). */
interface DataLabel {
    placed: boolean;
    newOpacity: number;
    alignAttr: { x: number; y: number };
    parentGroup: { translateX: number; translateY: number };
    box: boolean;
    padding: number;
    width: number;
    height: number;
    element: DataLabelElement;
}

/** Highcharts Point with internal properties used by this plugin. */
interface ColumnPoint extends Point {
    dataLabel?: DataLabel;
    negative?: boolean;
}

/** Highcharts Series with typed column points. */
interface ColumnSeries extends Series {
    data: Array<ColumnPoint>;
}

/**
 * @classdesc Class for ui-chart-column fix-overlap-labels.
 * @alias UIChartFixOverlapLabels
 * @implements {ComponentPlugin}
 * @param {HTMLElement} target
 */
export class UIChartFixOverlapLabels implements ComponentPlugin {
    private static readonly MinPointLength = 13;

    constructor(private readonly target: UIChartColumn) {}

    /**
     * Rectangle intersection check.
     */
    static intersectRect(
        x1: number,
        y1: number,
        w1: number,
        h1: number,
        x2: number,
        y2: number,
        w2: number,
        h2: number
    ): boolean {
        return !(x2 > x1 + w1 || x2 + w2 < x1 || y2 > y1 + h1 || y2 + h2 < y1);
    }

    /**
     * Moves label down.
     */
    fixOverlapLabels(): void {
        const chart = this.target.chart;

        if (!chart) {
            return;
        }

        const series: Array<ColumnSeries> = chart.series;
        const mpl =
            (
                chart.userOptions?.plotOptions
                    ?.series as ExtendedPlotSeriesOptions
            )?.['min-point-length'] ?? UIChartFixOverlapLabels.MinPointLength;

        for (let i = 1; i < series.length; ++i) {
            for (let j = 0; j < series.length; ++j) {
                if (!series[i - 1].data[j] || !series[i].data[j]) {
                    return;
                }

                const label1 = series[i - 1].data[j].dataLabel;
                const label2 = series[i].data[j].dataLabel;

                if (
                    label1 &&
                    label2 &&
                    label1.placed &&
                    label2.placed &&
                    label1.newOpacity !== 0 &&
                    label2.newOpacity !== 0
                ) {
                    const pos1 = label1.alignAttr;
                    const pos2 = label2.alignAttr;
                    const parent1 = label1.parentGroup;
                    const parent2 = label2.parentGroup;
                    const padding = 2 * (label1.box ? 0 : label1.padding);

                    const isIntersecting =
                        UIChartFixOverlapLabels.intersectRect(
                            pos1.x + parent1.translateX,
                            pos1.y + parent1.translateY,
                            label1.width - padding,
                            label1.height - padding,
                            pos2.x + parent2.translateX,
                            pos2.y + parent2.translateY,
                            label2.width - padding,
                            label2.height - padding
                        );

                    if (isIntersecting) {
                        const y1 =
                            Number(label1.element.getAttribute('y')) || 0;
                        const y2 =
                            Number(label2.element.getAttribute('y')) || 0;
                        const k1 = series[i - 1].data[j].negative ? -1 : 1;
                        const k2 = series[i].data[j].negative ? -1 : 1;
                        label1.element.setAttribute(
                            'y',
                            (y2 - mpl * k2) / 2.0 + mpl - k1 * 2
                        );
                        label2.element.setAttribute(
                            'y',
                            (y1 + mpl * k1) / 2.0 + mpl - k2 * 2
                        );
                    }
                }
            }
        }
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        whenDOMReady(() => {
            this.target.chart?.update({
                plotOptions: {
                    series: {
                        events: {
                            afterAnimate: this.fixOverlapLabels.bind(this),
                        },
                    },
                },
            });
        }, false);
    }
}

UIChartColumn.registerPlugin('fix-overlap-labels', UIChartFixOverlapLabels);
